import { Search } from 'lucide-react'
import { useState, type ReactNode, type Ref } from 'react'
import { createPortal } from 'react-dom'
import { FilterButton, type Filter } from '@/components/Filters'
import { SlidingTabs, TabsEnd } from '@/components/modern/SwipeTabs'
import { BarRow } from '@/components/nav/BarRow'
import { page } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useFrame } from '@/lib/frame'
import { useIsPhone } from '@/lib/platform'
import { cn } from '@/lib/utils'

export type PeopleTab = 'people' | 'circles'
const TABS: { key: PeopleTab; label: string }[] = [
  { key: 'people', label: 'People' },
  { key: 'circles', label: 'Circles' },
]

// People's top row, like Photos' and Moments': the People and Circles tabs, then search and Filter for the People
// tab, and `end` (the family tree, Circle settings). On a phone it sits in the top bar, with search and Filter beside
// the "+"; search opens from its icon and takes the row while it's open.
export function PeopleTabs({
  tab,
  onTab,
  search,
  onSearch,
  filters,
  find = tab === 'people',
  end,
  ref,
}: {
  tab: PeopleTab
  onTab: (tab: PeopleTab) => void
  search: string
  onSearch: (search: string) => void
  filters: Filter[]
  // Whether search and Filter are offered: on the People tab, unless it's showing the family tree.
  find?: boolean
  end?: ReactNode
  ref?: Ref<HTMLDivElement>
}) {
  const phone = useIsPhone()
  const { actionSlot } = useFrame()
  const [searching, setSearching] = useState(false)
  const inTop = phone && !!actionSlot
  const field = (
    <div className="relative min-w-0 flex-1 nav:max-w-sm">
      <Search className="absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        type="search"
        aria-label="Find someone"
        placeholder="Search"
        autoFocus={searching}
        className="rounded-full border-transparent bg-secondary pl-9 shadow-none dark:bg-secondary"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
      />
    </div>
  )
  const filter = filters.length > 0 && (
    <FilterButton
      title="Filter"
      className={inTop ? 'order-first bg-transparent hover:bg-transparent active:opacity-60' : undefined}
      filters={filters}
    />
  )

  if (inTop && searching && find)
    return (
      <BarRow ref={ref} pinned>
        {field}
        <Button
          variant="ghost"
          className="shrink-0 pointer-coarse:min-h-11"
          onClick={() => {
            setSearching(false)
            onSearch('')
          }}
        >
          Cancel
        </Button>
      </BarRow>
    )

  return (
    <BarRow ref={ref} pinned className={cn(page, 'flex items-center gap-3 max-sm:px-0')}>
      <SlidingTabs label="People view" tabs={TABS} active={tab} onChoose={onTab} />
      {find && !inTop && field}
      <TabsEnd>
        {find &&
          (inTop
            ? createPortal(
                <>
                  <button
                    type="button"
                    aria-label="Search"
                    onClick={() => setSearching(true)}
                    className="order-first grid size-11 shrink-0 place-items-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:opacity-60"
                  >
                    <Search className="size-4.5" aria-hidden />
                  </button>
                  {filter}
                </>,
                actionSlot,
              )
            : filter)}
        {end}
      </TabsEnd>
    </BarRow>
  )
}
